import { addWorkingDays } from "@ryoma/engine";
import type { AuditRow, Db, UploadRow } from "../data/db.ts";
import { statusPill } from "./audits.ts";
import { action, date, h, mount, notice, num, stat } from "./dom.ts";
import { toast } from "./toast.ts";
import { uploadWizard } from "./upload.ts";

/** Extra sections (levers, report) plug in here so this file stays about the data. */
export type AuditSection = (ctx: { db: Db; audit: AuditRow; upload: UploadRow | null; refresh: () => void }) => Promise<HTMLElement>;
const sections: AuditSection[] = [];
export function registerAuditSection(s: AuditSection): void {
  sections.push(s);
}

const today = () => new Date().toISOString().slice(0, 10);

export async function serviceOptions(db: Db): Promise<{ code: string; label: string }[]> {
  const cards = await db.listRateCards();
  const seen = new Set<string>();
  const out: { code: string; label: string }[] = [];
  for (const c of cards) {
    for (const s of c.body.services) {
      if (seen.has(s.code)) continue;
      seen.add(s.code);
      out.push({ code: s.code, label: `${s.name} (${s.code}, ${c.id}@${c.version})` });
    }
  }
  return out;
}

export async function auditView(db: Db, id: string): Promise<HTMLElement> {
  const root = h("section", { class: "page" });

  const render = async () => {
    const audit = await db.getAudit(id);
    if (!audit) {
      mount(root, notice("error", "Audit not found, or you do not have access."));
      return;
    }
    const upload = await db.currentUpload(id);
    const dataBox = h("div", { class: "card" });

    const showSummary = () => {
      if (!upload) {
        mount(dataBox, h("h2", null, "1. Client data"), notice("info", "No data yet."),
          h("div", { class: "row-actions" }, h("button", { type: "button", class: "btn btn-primary", onClick: openWizard }, "Upload client data")));
        return;
      }
      const complete = action("Mark data complete", async () => {
        if (upload.error_count > 0 && !confirm(`${upload.error_count} rows were left out with errors. Mark the data complete anyway?`)) return;
        const start = today();
        await db.updateAudit(id, { data_complete_at: new Date().toISOString(), report_due: addWorkingDays(start, 5) });
        await render();
      }, { primary: !audit.data_complete_at });

      mount(dataBox,
        h("h2", null, "1. Client data"),
        h("div", { class: "stats" },
          stat("Parcels", num(upload.parcel_count)),
          stat("Rows in file", num(upload.source_rows)),
          stat("Rows left out", num(upload.source_rows - upload.kept_rows), undefined, upload.source_rows > upload.kept_rows ? "warn" : undefined),
          stat("Warnings", num(upload.warning_count), undefined, upload.warning_count ? "warn" : undefined),
        ),
        h("p", { class: "muted small" },
          `${upload.file_name} · uploaded ${date(upload.uploaded_at)} · engine ${upload.engine_version} · data `,
          h("code", { title: upload.dataset_hash }, upload.dataset_hash.slice(0, 12))),
        audit.data_complete_at
          ? notice("ok", `Data marked complete ${date(audit.data_complete_at)}. Report due `, h("strong", null, date(audit.report_due)), " (5 working days).")
          : notice("info", "When the client has sent everything, mark the data complete. That starts the 5-working-day clock."),
        h("div", { class: "row-actions" },
          h("button", { type: "button", class: "btn", onClick: openWizard }, "Replace data"),
          audit.data_complete_at ? "" : complete,
        ),
        audit.purge_after ? h("p", { class: "muted small" }, `Cleaned client data is deleted after ${date(audit.purge_after)} (90 days after implementation ends).`) : "",
      );
    };

    const openWizard = async () => {
      const codes = await serviceOptions(db);
      mount(dataBox, uploadWizard({
        db, auditId: id, serviceCodes: codes,
        previousServiceMap: upload?.service_map ?? {},
        onSaved: () => void render(),
        onCancel: showSummary,
      }));
    };

    showSummary();
    const extra = await Promise.all(sections.map((s) => s({ db, audit, upload, refresh: () => void render() })));
    mount(root,
      h("p", { class: "crumbs" }, h("a", { href: "#/audits" }, "Audits"), " / ", audit.client_name),
      h("div", { class: "page-head" },
        h("h1", null, audit.title),
        statusPill(audit.status),
        audit.report_due ? h("span", { class: "due" }, "Report due ", h("strong", null, date(audit.report_due))) : "",
      ),
      h("p", { class: "muted" }, audit.client_name),
      dataBox,
      ...extra,
      dangerZone(db, audit),
    );
  };

  await render();
  return root;
}

/** Delete the audit, and the client too when this was their last audit. Kept in the activity log. */
function dangerZone(db: Db, audit: AuditRow): HTMLElement {
  const del = action("Delete this audit", async () => {
    if (!confirm(`Delete "${audit.title}" for ${audit.client_name}? This removes its client data, lever results, report records and implementation plan. It cannot be undone. The activity log keeps a record that it was deleted.`)) return;
    const others = (await db.listAudits()).filter((a) => a.client_id === audit.client_id && a.id !== audit.id);
    await db.deleteAudit(audit.id);
    let clientGone = false;
    if (!others.length && confirm(`${audit.client_name} has no other audits. Delete the client as well? Any lead that became this client is kept.`)) {
      await db.deleteClient(audit.client_id);
      clientGone = true;
    }
    toast(clientGone ? `Deleted the audit and ${audit.client_name}` : "Audit deleted");
    location.hash = "#/audits";
  });
  del.querySelector("button")!.classList.add("btn-danger");
  return h("details", { class: "card danger-zone" },
    h("summary", null, "History and delete"),
    h("p", null, h("a", { href: `#/activity?record=${audit.id}` }, "See everything added, changed or deleted on this audit")),
    h("p", { class: "muted small" }, "For an audit created by mistake, or when a client asks us to erase their data early. Otherwise their cleaned data is removed on its own 90 days after implementation ends."),
    h("div", { class: "row-actions" }, del),
  );
}
